import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MentorReviewBookingDialogComponent } from '../mentor-review-booking-dialog/mentor-review-booking-dialog.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MentorService } from '@edtech-ui/core/services/mentor-management/mentor.service';
import { catchError, filter, finalize, of, Subscription, tap } from 'rxjs';
import { GetAppointmentType } from '@edtech-ui/core/models/mentors-sessions/types-of-sessions';
import { ToastrService } from 'ngx-toastr';
import { GetAvailableSlot } from '@edtech-ui/core/models/mentors-sessions/available-slots-time-session';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MentorSessionDurationPricing, MentorSessionPricingRequest } from '@edtech-ui/core/models/mentors-sessions/get-all-prices';
import { MentorSessionBookingPayload } from '@edtech-ui/core/models/mentors-sessions/post-confirm-reserve-slots';
import { Router } from '@angular/router';
import { MentorSessionFreePayment } from '@edtech-ui/constants/mentor-session-free-payment.enum';
import flatpickr from 'flatpickr';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { normalizeTimeZone } from '@edtech-ui/core/utils/date-time.util';


@Component({
	selector: 'app-mentor-book-session-dialog',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, MatProgressSpinnerModule, HasPermissionDirective],
	templateUrl: './mentor-book-session-dialog.component.html',
	styleUrl: './mentor-book-session-dialog.component.scss'
})
export class MentorBookSessionDialogComponent implements OnInit, AfterViewInit, OnDestroy {

	selectedDuration: number = 0;
	bookSessionForm: FormGroup;
	isLoading = false;
	subscription = new Subscription();
	appointmentTypes: GetAppointmentType[] = [];
	availableTimeSlot: GetAvailableSlot[] = [];
	mentorId: string = '';
	first_name = '';
	last_name = '';
	designation = '';
	company = '';
	sessionPriceType: MentorSessionDurationPricing[] = [];
	@ViewChild('dateInput') dateInput!: ElementRef;
	private flatpickrInstance: flatpickr.Instance | null = null;
	PermissionForStudents = PermissionForStudents;

	// Custom dropdown state
	sessionTypeOpen = false;
	durationOpen = false;
	timeOpen = false;

	constructor(
		private dialog: MatDialog,
		public dialogRef: MatDialogRef<MentorBookSessionDialogComponent>,
		private _mentorService: MentorService,
		private _toastr: ToastrService,
		private router: Router,
		@Inject(MAT_DIALOG_DATA) public data: {
			mentor_id: string, first_name: string, last_name: string, designation: string, company: string, formState?: any;
		}
	) {
		this.mentorId = data.mentor_id;
		this.first_name = data.first_name;
		this.last_name = data.last_name;
		this.designation = data.designation;
		this.company = data.company;

		this.bookSessionForm = new FormGroup({
			appointmentType: new FormControl('', Validators.required),
			date: new FormControl('', Validators.required),
			time: new FormControl('', Validators.required),
			duration: new FormControl('', Validators.required),
			topic: new FormControl('', [Validators.required, Validators.minLength(10)]),
			product_id: new FormControl('', Validators.required),
			price_display: new FormControl(''),
			pricing_id: new FormControl(''),
		});
		if (data.formState) {
			this.bookSessionForm.patchValue(data.formState);
			this.selectedDuration = data.formState.duration;
		}
		if (data.formState?.date && data.formState?.duration) {
			this.getAllAvailableTimeSlots(data.formState.date, data.formState.duration);
		}
	}

	ngOnInit(): void {
		this.getUsersSessionTypes();
		this.bookSessionForm.get('appointmentType')?.valueChanges.subscribe((value) => {
			if (value && value.id) {
				this.getMentorSessionPricing(value.id);
			}
		});
	}


	ngAfterViewInit(): void {
		const today = new Date();
		const allowedDates = this.getNext15WorkingDays(today);

		this.flatpickrInstance = flatpickr(this.dateInput.nativeElement, {
			dateFormat: 'Y-m-d',
			clickOpens: true,
			allowInput: false,

			closeOnSelect: true,

			disable: [
				(date: Date) => {
					return !allowedDates.some(d =>
						d.getDate() === date.getDate() &&
						d.getMonth() === date.getMonth() &&
						d.getFullYear() === date.getFullYear()
					);
				}
			],

			onChange: (selectedDates: Date[]) => {
				if (selectedDates.length) {
					const selectedDate = selectedDates[0];
					const formatted = flatpickr.formatDate(selectedDate, 'Y-m-d');
					this.bookSessionForm.patchValue({ date: formatted });
					this.onDateChange();
				}
			},

			onClose: () => {
			}
		});
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		const target = event.target as HTMLElement;
		if (!target.closest('.custom-select-wrapper')) {
			this.sessionTypeOpen = false;
			this.durationOpen = false;
			this.timeOpen = false;
		}
	}

	close() {
		this.dialogRef.close();
	}

	onOverlayClick(event: MouseEvent): void {
		if ((event.target as HTMLElement).classList.contains('overlay')) {
			this.close();
		}
	}


	toggleSessionType(): void {
		this.sessionTypeOpen = !this.sessionTypeOpen;
		this.durationOpen = false;
		this.timeOpen = false;
	}

	selectSessionType(appointment: GetAppointmentType): void {
		this.bookSessionForm.get('appointmentType')?.setValue(appointment);
		this.sessionTypeOpen = false;
	}

	toggleDuration(): void {
		this.durationOpen = !this.durationOpen;
		this.sessionTypeOpen = false;
		this.timeOpen = false;
	}

	selectDurationOption(sessionPrice: MentorSessionDurationPricing, event: MouseEvent): void {
		event.stopPropagation();
		this.onDurationChange({ target: { value: sessionPrice.duration_minutes } });
		this.durationOpen = false;
	}

	getSelectedDurationLabel(): string {
		const val = this.bookSessionForm.get('duration')?.value;
		if (!val) return '';
		const match = this.sessionPriceType.find(x => x.duration_minutes == val);
		return match ? match.display_label : '';
	}

	toggleTime(): void {
		this.timeOpen = !this.timeOpen;
		this.sessionTypeOpen = false;
		this.durationOpen = false;
	}

	selectTime(availableTime: GetAvailableSlot): void {
		this.bookSessionForm.get('time')?.setValue(availableTime);
		this.timeOpen = false;
	}

	PostbookSessionForm() {
		if (this.bookSessionForm.invalid) {
			this.bookSessionForm.markAllAsTouched();
			this._toastr.warning('Please fill all required fields.');
			return;
		}
		this.isLoading = true;
		const selectedSlot = this.bookSessionForm.value.time;

		const payload = {
			mentor_id: this.mentorId,
			availability_id: selectedSlot.availability_id,
			appointment_type_id: this.bookSessionForm.value.appointmentType.id,
			appointment_date: this.bookSessionForm.value.date,
			start_time: selectedSlot.start_time,
			end_time: selectedSlot.end_time,
			duration_minutes: this.bookSessionForm.value.duration,
			topics_to_discuss: this.bookSessionForm.value.topic,
			appointment_type_name: this.bookSessionForm.value.appointmentType.type_name,
			first_name: this.first_name,
			last_name: this.last_name,
			designation: this.designation,
			product_id: this.bookSessionForm.value.product_id,
			company: this.company,
			price_display: this.bookSessionForm.value.price_display,
			pricing_id: this.bookSessionForm.value.pricing_id,
			display: selectedSlot.display
		}

		this._mentorService.postMentorSessionReserveSlot(payload).pipe(
			tap(response => {
				if (response.Success) {
					const bookingData = response.Data;
					if (!bookingData.requires_payment || bookingData.payment_status === MentorSessionFreePayment.Payment_Free) {
						this._toastr.success("Booking confirmed! Check your email for meeting link.");
						this.dialogRef.close();
						this.router.navigate(['/student-dashboard/my-sessions']);
						return;
					}
					const expiresInSeconds = bookingData.expires_in_seconds;
					this._mentorService.mentorBookingId.next(response.Data.booking_id);
					this._mentorService.studentName.next(response.Data.student_name);
					this.openReviewBookingForm({
						...payload, formState: this.bookSessionForm.value, expires_in_seconds: expiresInSeconds
						, booking_id: response.Data.booking_id,
					});
				} else {
					this._toastr.error(response.Message);
				}
			}),
			finalize(() => {
				this.isLoading = false;
			}),
			catchError(() => {
				this._toastr.error('Something went wrong while reserving the slots');
				return of(null);
			})
		).subscribe();

	}

	openReviewBookingForm(payload: MentorSessionBookingPayload) {
		this.dialog.open(MentorReviewBookingDialogComponent, {
			data: payload,
			autoFocus: false
		});
		this.close();
	}
	getUsersSessionTypes() {
		this.subscription.add(
			this._mentorService.getUsersSessionsType().pipe(
				tap(response => {
					if (response.Success) {
						this.appointmentTypes = response.Data.appointment_types;
						const savedTypeId = this.bookSessionForm.value.appointmentType?.id;
						if (savedTypeId) {
							const match = this.appointmentTypes.find(type => type.id === savedTypeId);
							if (match) {
								this.bookSessionForm.get('appointmentType')?.setValue(match);
							}
						}
					} else {
						this._toastr.error(response.Message)
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong while fetching the session types');
					return of(null);
				})
			).subscribe()
		);
	}

	getAllAvailableTimeSlots(selectedDate: string, selectedDuration: number) {
		this.subscription.add(
			this._mentorService.getAllAvailableTimeSlots(this.mentorId, selectedDate, selectedDuration).pipe(
				tap(response => {
					if (response.Success) {
						this.availableTimeSlot = response.Data.available_slots.map(slot => ({
							...slot,
							display: this.formatTime(slot.start_time)
						}));
						const savedSlotId = this.bookSessionForm.value.time?.availability_id;
						if (savedSlotId) {
							const match = this.availableTimeSlot.find(slot => slot.availability_id == savedSlotId);
							if (match) {
								this.bookSessionForm.get('time')?.setValue(match);
							}
						}
					} else {
						this._toastr.error(response.Message)
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong while fetching the time slots');
					return of(null);
				})
			).subscribe());
	}

	onDateChange() {
		const selectedDate = this.bookSessionForm.get('date')?.value;
		const selectedDuration = this.selectedDuration;
		if (selectedDate && selectedDuration) {
			this.getAllAvailableTimeSlots(selectedDate, selectedDuration)
		}
		this.bookSessionForm.get('duration')?.reset('', { emitEvent: false });
		this.bookSessionForm.get('time')?.reset('', { emitEvent: false });

		this.selectedDuration = 0;
		this.availableTimeSlot = [];
	}

	selectDuration(duration: number) {
		this.selectedDuration = duration;
		this.bookSessionForm.patchValue({ duration });
		const selectedDate = this.bookSessionForm.get('date')?.value;
		if (selectedDate && this.selectedDuration) {
			this.getAllAvailableTimeSlots(selectedDate, this.selectedDuration);
		}
	}

	getMentorSessionPricing(appointmentTypeId: number) {
		if (!this.mentorId) {
			console.warn('Mentor ID is missing. Pricing cannot be fetched.');
			return;
		}
		const request: MentorSessionPricingRequest = {
			appointment_type_id: appointmentTypeId,
			mentor_id: this.mentorId
		};
		this.subscription.add(
			this._mentorService.getMentorSessionPricing(request).pipe(
				tap(response => {
					if (response.Success) {
						this.sessionPriceType = response.Data;
					} else {
						this._toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong');
					return of(null);
				}),
			).subscribe()
		)
	}

	onDurationChange(event: any) {
		const duration = Number(event.target.value);
		this.selectedDuration = duration;
		this.bookSessionForm.patchValue({
			duration: duration
		});
		const selectedItem = this.sessionPriceType.find(
			(x: any) => x.duration_minutes == this.selectedDuration);

		const selectedDate = this.bookSessionForm.get('date')?.value;
		if (selectedDate && duration) {
			this.getAllAvailableTimeSlots(selectedDate, duration);
		}

		if (selectedItem) {
			this.bookSessionForm.patchValue({
				product_id: selectedItem.product_id,
				price_display: selectedItem.price_display,
				pricing_id: selectedItem.pricing_id
			});
		}
	}

	private getNext15WorkingDays(start: Date): Date[] {
		const result: Date[] = [];
		const current = new Date(start);
		while (result.length < 15) {
			const day = current.getDay();
			if (day !== 0 && day !== 6) {
				result.push(new Date(current));
			}
			current.setDate(current.getDate() + 1);
		}
		return result;
	}


	private formatTime(isoString: string): string {
		const date = new Date(isoString);
		const timeZone = normalizeTimeZone(localStorage.getItem('user_time_zone'));

		return date.toLocaleTimeString('en-US', {
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
			timeZone: timeZone
		});
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}